import { useQuery } from '@tanstack/react-query';
import { request } from '../api/client';
import { productRepository, type ProductFilters } from './services/productRepository';
import { galleryItems, galleryCategories } from './data/gallery';
import type { GalleryItem, GalleryCategory } from './types/gallery';

/**
 * React-Query hooks over the live productRepository. The catalogue changes rarely, so everything is
 * cached with a long staleTime. Components consume these instead of calling the repository directly,
 * getting loading/error states + shared caching for free. Backend remains the single source of truth.
 */
const LONG = { staleTime: 5 * 60_000, gcTime: 30 * 60_000 };

export function useProducts() {
  return useQuery({ queryKey: ['site', 'products'], queryFn: () => productRepository.getAllProducts(), ...LONG });
}

export function useProduct(slug: string | undefined) {
  return useQuery({
    queryKey: ['site', 'product', slug],
    queryFn: async () => (await productRepository.getProductBySlug(slug!)) ?? null,
    enabled: !!slug,
    ...LONG,
  });
}

/** The whole catalogue with detail sections + documents (document library, approvals register). */
export function useProductsDetailed() {
  return useQuery({
    queryKey: ['site', 'products-detailed'],
    queryFn: () => productRepository.getAllProductsDetailed(),
    ...LONG,
  });
}

export function useFeaturedProducts(limit = 6) {
  return useQuery({ queryKey: ['site', 'featured', limit], queryFn: () => productRepository.getFeaturedProducts(limit), ...LONG });
}

export function useProductsByCategory(categorySlug: string | undefined) {
  return useQuery({
    queryKey: ['site', 'products-by-category', categorySlug],
    queryFn: () => productRepository.getProductsByCategory(categorySlug!),
    enabled: !!categorySlug,
    ...LONG,
  });
}

export function useCategories() {
  return useQuery({ queryKey: ['site', 'categories'], queryFn: () => productRepository.getCategories(), ...LONG });
}

export function useCategory(slug: string | undefined) {
  return useQuery({
    queryKey: ['site', 'category', slug],
    queryFn: async () => (await productRepository.getCategoryBySlug(slug!)) ?? null,
    enabled: !!slug,
    ...LONG,
  });
}

export function useCropSolutions() {
  return useQuery({ queryKey: ['site', 'crops'], queryFn: () => productRepository.getCropSolutions(), ...LONG });
}

export function useCrop(slug: string | undefined) {
  return useQuery({
    queryKey: ['site', 'crop', slug],
    queryFn: async () => (await productRepository.getCropBySlug(slug!)) ?? null,
    enabled: !!slug,
    ...LONG,
  });
}

export function useFilterFacets() {
  return useQuery({ queryKey: ['site', 'facets'], queryFn: () => productRepository.getFilterFacets(), ...LONG });
}

export function useFilteredProducts(filters: ProductFilters) {
  return useQuery({
    queryKey: ['site', 'search', filters],
    queryFn: () => productRepository.searchProducts(filters),
    ...LONG,
  });
}

/**
 * Gallery data. Live, photo-backed events from `/public/events` are the source of truth; until any are
 * published they gracefully fall back to the branded placeholder tiles (data/gallery.ts) so the section
 * renders exactly like the design instead of disappearing. `placeholder` tells the UI to show the
 * "branded placeholders pending real event photography" note.
 */
const EVENT_TYPE_LABEL: Record<string, string> = {
  RETAILER_MEET: 'Retailer Meetings',
  FARMER_MEET: 'Farmer Meetings',
  TRAINING: 'Training',
  SEMINAR: 'Seminars',
  PRODUCT_LAUNCH: 'Product Launches',
  FIELD_DAY: 'Field Days',
  MEETING: 'Meetings',
  OTHER: 'Other',
};

export function useGalleryPreview(limit = 6) {
  return useQuery({
    queryKey: ['site', 'gallery-preview', limit],
    queryFn: async (): Promise<{ items: GalleryItem[]; placeholder: boolean }> => {
      const res = await request<{ data: any[] }>({ url: '/public/events', params: { page: 1, pageSize: limit } });
      const rows = (res?.data ?? []).filter((e: any) => e.coverUrl);
      if (!rows.length) return { items: galleryItems.slice(0, limit), placeholder: true };
      const items: GalleryItem[] = rows.map((e: any, i: number) => ({
        id: String(e.id),
        src: e.coverUrl as string,
        width: 1000,
        height: i % 3 === 0 ? 1300 : 800,
        category: (e.type as string) || 'OTHER',
        title: String(e.title ?? 'Event'),
        caption: (e.venue as string) || undefined,
      }));
      return { items, placeholder: false };
    },
    ...LONG,
  });
}

export function useGallery() {
  return useQuery({
    queryKey: ['site', 'gallery'],
    queryFn: async (): Promise<{ items: GalleryItem[]; categories: GalleryCategory[]; placeholder: boolean }> => {
      const res = await request<{ data: any[] }>({ url: '/public/events', params: { page: 1, pageSize: 48 } });
      const rows = (res?.data ?? []).filter((e: any) => e.coverUrl);
      if (!rows.length) return { items: galleryItems, categories: galleryCategories, placeholder: true };
      const items: GalleryItem[] = rows.map((e: any, i: number) => ({
        id: String(e.id),
        src: e.coverUrl as string,
        width: 1000,
        height: i % 3 === 0 ? 1300 : 800, // alternating tall/short → masonry rhythm
        category: (e.type as string) || 'OTHER',
        title: String(e.title ?? 'Event'),
        caption: (e.venue as string) || undefined,
      }));
      const present = Array.from(new Set(items.map((i) => i.category)));
      const categories = [{ slug: 'all', name: 'All' }, ...present.map((t) => ({ slug: t, name: EVENT_TYPE_LABEL[t] ?? t }))];
      return { items, categories, placeholder: false };
    },
    ...LONG,
  });
}
